import type * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Select";
import type { DataTablePaginationMeta } from "./types";

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;

export interface DataTablePaginationProps {
  meta: DataTablePaginationMeta;
  pageSize: number;
  displayedCount: number;
  onPageChange: (pageIndex: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: ReadonlyArray<number> | undefined;
  rowsPerPageLabel?: string | undefined;
  labels: {
    showingCount: (count: number, total: number) => React.ReactNode;
    previousPage: string;
    nextPage: string;
    rowsPerPage?: string | undefined;
    rowsPerPageLabel?: string | undefined;
  };
  className?: string | undefined;
}

function getPageItems(
  currentPage: number,
  totalPages: number,
): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [
      1,
      "ellipsis",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }
  return [
    1,
    "ellipsis",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "ellipsis",
    totalPages,
  ];
}

export function DataTablePagination({
  meta,
  pageSize,
  displayedCount,
  onPageChange,
  onPreviousPage,
  onNextPage,
  onPageSizeChange,
  pageSizeOptions,
  rowsPerPageLabel: rowsPerPageLabelProp,
  labels,
  className,
}: DataTablePaginationProps) {
  const canPreviousPage = meta.currentPage > 1;
  const canNextPage = meta.currentPage < meta.totalPages && meta.totalPages > 0;
  const pageItems = getPageItems(meta.currentPage, meta.totalPages);

  const resolvedRowsPerPageLabel =
    rowsPerPageLabelProp ?? labels.rowsPerPageLabel ?? "Rows per page";
  const baseOptions = pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const resolvedPageSizeOptions = baseOptions.includes(pageSize)
    ? baseOptions
    : [...baseOptions, pageSize].sort((a, b) => a - b);

  return (
    <div
      className={cn(
        "flex min-h-14 w-full flex-col items-stretch gap-3 border-t border-table-cell-bottom-border bg-table-column-header px-3 py-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 md:px-4 md:py-2",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <div className="text-body-footnote text-text-placeholder">
          {labels.showingCount(displayedCount, meta.totalCount)}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-body-footnote text-text-placeholder">
            {resolvedRowsPerPageLabel}
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => {
              if (value) {
                const nextSize = Number(value);
                if (!Number.isNaN(nextSize)) {
                  onPageSizeChange?.(nextSize);
                }
              }
            }}
          >
            <SelectTrigger
              aria-label={resolvedRowsPerPageLabel}
              className="h-10 w-auto justify-center gap-1 border-secondary-button-outline bg-secondary-button-background px-3 text-body-s-r text-secondary-button-text hover:bg-container-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary active:scale-[0.98] transition-[background-color,color,border-color,scale] duration-140 ease-emphasized [&_svg]:opacity-100 [&_svg]:text-secondary-button-icon"
            >
              <SelectValue placeholder={String(pageSize)}>
                {pageSize}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[5rem]">
              {resolvedPageSizeOptions.map((size) => (
                <SelectItem
                  key={size}
                  value={String(size)}
                  className="text-body-s-r"
                >
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <nav
        aria-label={labels.rowsPerPage ?? "Pagination"}
        // horizontal scroll keeps page buttons from wrapping/squishing on narrow screens
        className="flex w-full items-center gap-1 overflow-x-auto [&>*]:shrink-0 md:w-auto md:overflow-visible"
      >
        <button
          type="button"
          aria-label={labels.previousPage}
          disabled={!canPreviousPage}
          onClick={onPreviousPage}
          className="inline-flex size-8 items-center justify-center rounded-md border border-container-default-border bg-container-default-background text-text transition-[background-color,scale] duration-140 ease-emphasized hover:bg-container-hover active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40 disabled:active:scale-100"
        >
          <ChevronLeft className="size-4 rtl:rotate-180" />
        </button>
        {pageItems.map((item, index) => {
          if (item === "ellipsis") {
            return (
              <span
                key={`ellipsis-${index}`}
                className="inline-flex size-8 items-center justify-center text-body-s-b text-text-placeholder select-none"
              >
                ...
              </span>
            );
          }
          const isActive = item === meta.currentPage;
          return (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item - 1)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "inline-flex size-8 items-center justify-center rounded-md text-body-s-b transition-[background-color,color,scale] duration-140 ease-emphasized active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
                isActive
                  ? "bg-primary-button-background text-primary-button-text"
                  : "text-text hover:bg-container-hover",
              )}
            >
              {item}
            </button>
          );
        })}
        <button
          type="button"
          aria-label={labels.nextPage}
          disabled={!canNextPage}
          onClick={onNextPage}
          className="inline-flex size-8 items-center justify-center rounded-md border border-container-default-border bg-container-default-background text-text transition-[background-color,scale] duration-140 ease-emphasized hover:bg-container-hover active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40 disabled:active:scale-100"
        >
          <ChevronRight className="size-4 rtl:rotate-180" />
        </button>
      </nav>
    </div>
  );
}
